import { Injectable } from '@angular/core';
import { ItemStatus } from '@app/enum/item-status.enum';
import { DirectusQuery } from '@app/interfaces/directus-query.interface';
import { DirectusSchema } from '@app/interfaces/directus-schema.interface';
import { Page } from '@app/models/page';
import { from, map, Observable } from 'rxjs';

import { DataService } from '../data/data.service';

@Injectable({
  providedIn: 'root'
})
export class PagesService extends DataService {
  protected override collection: keyof DirectusSchema = 'pages';
  protected override fields = [
    '*',
    'category.*',
    'contents.contents_id.*',
    'contents.contents_id.groups.groups_id.*',
    'groups.groups_id.id,groups.groups_id.title,groups.groups_id.externalId',
    'links_additional.links_additional_id.*',
    'links_additional.links_additional_id.groups.groups_id.*',
    'links_conditions.links_conditions_id.*',
    'links_conditions.links_conditions_id.groups.groups_id.*',
    'links_sales_material.links_sales_material_id.*',
    'links_sales_material.links_sales_material_id.groups.groups_id.*'
  ];

  /*
   * getItems is used to override the extendsQuery for readItems in DataService
   */
  public override getItems<Page>(query: DirectusQuery): Observable<Page[]> {
    // override the default query for getItems
    query.filter['status'] = {
      _in: [ItemStatus.Published, ItemStatus.Archived]
    };
    query.sort = 'sort';
    return super
      .getItems(this.extendQuery(query))
      .pipe(map((pages: unknown[]) => (Array.isArray(pages) ? pages.map(this.filterContentsLinks) : [])));
  }

  /*
   * Method is used to search for pages by title, description, and content blocks
   */
  public search<T = Page>(query: string, limit = 10): Observable<T[]> {
    const searchQuery: DirectusQuery = {
      filter: {
        status: {
          _in: [ItemStatus.Published, ItemStatus.Archived]
        },
        _or: [
          { title: { _icontains: query } },
          { description: { _icontains: query } },
          { contents: { contents_id: { title: { _icontains: query } } } },
          { contents: { contents_id: { text: { _icontains: query } } } }
        ]
      },
      fields: ['id', 'slug', 'title', 'category.title'],
      limit,
      sort: 'sort'
    };
    return from(this.directusService.readItems<T>(this.collection, this.extendQuery(searchQuery)));
  }

  /*
   * getItem is used to override the default query for getItem
   */
  public override getItem<Page>(id: string, force?: boolean, isPreview?: boolean): Observable<Page | null> {
    // override the default query for getItem
    this.query.filter['status'] = {
      _in: [ItemStatus.Published, ItemStatus.Archived]
    };
    // TODO find a better way to handle those situation
    // in case category was set on the list page and we are on the detail page, we need to remove the category filter
    if (this.query && this.query.filter && this.query.filter['category']) {
      delete this.query.filter['category'];
    }
    return super
      .getItem(id, force, isPreview)
      .pipe(map((page: unknown) => (page ? this.filterContentsLinks(page) : page))) as Observable<Page>;
  }

  /*
   * Method is used to override the default query for readItems, depending on m2m structure for groups
   */
  protected override extendQuery(query: DirectusQuery, isPreview?: boolean): DirectusQuery {
    // set fields from current service
    query.fields = this.fields;
    // set groups from auth service, only if extendQueryWithGroups is true
    const groups = this.authService.getPermissionGroups();
    if (groups.length > 0 && !isPreview) {
      if (!query.filter) {
        query.filter = {};
      }
      const groupsOr = [
        {
          groups: {
            groups_id: {
              externalId: {
                _in: groups
              }
            }
          }
        },
        {
          groups: {
            groups_id: {
              _empty: true
            }
          }
        }
      ];
      if (query.filter['_or']) {
        const existingOr = query.filter['_or'];
        delete query.filter['_or'];
        query.filter['_and'] = [{ _or: existingOr }, { _or: groupsOr }];
      } else {
        query.filter['_or'] = groupsOr;
      }
      query.deep = {
        contents: {
          contents_id: this.getDeepFilter(groups)
        },
        links_additional: {
          links_additional_id: this.getDeepFilter(groups)
        },
        links_conditions: {
          links_conditions_id: this.getDeepFilter(groups)
        },
        links_sales_material: {
          links_sales_material_id: this.getDeepFilter(groups)
        }
      };
    }
    return query;
  }

  private filterContentsLinks(page: any): any {
    // TODO - find a better way to query in the first place, see getDeepFilter
    // workaround because contents are returned like { contents_id: null }
    page.contents = page.contents.filter((content: any) => content.contents_id !== null);
    page.links_additional = page.links_additional.filter((link: any) => link.links_additional_id !== null);
    page.links_conditions = page.links_conditions.filter((link: any) => link.links_conditions_id !== null);
    page.links_sales_material = page.links_sales_material.filter((link: any) => link.links_sales_material_id !== null);
    return page;
  }

  private getDeepFilter(groups: string[]): unknown {
    return {
      _filter: {
        status: {
          _in: [ItemStatus.Published]
        },
        groups: {
          groups_id: {
            externalId: {
              _in: groups
            }
          }
        }
      }
    };
  }
}
